// shared/application/guardarConHijos.js  (orquestación compartida: padre (+asistentes) -> eliminar imágenes -> subir imágenes). Sin JSX ni Swal.
// La usan useCapacitacionForm y useVisitaForm; los repositorios llegan por parámetro (costura para mocks).
import { DomainError } from '../domain/DomainError';
import { mensajeErrorSubida } from '../domain/media';
import { ejecutarEliminaciones } from './ejecutarEliminaciones';

/**
 * Guarda el padre (con sus asistentes en el MISMO payload) y luego sus imágenes.
 * Orden: (1) padre, (2) eliminaciones de imágenes guardadas marcadas (una a una), (3) subida de pendientes.
 * - `entidad`: texto con artículo para los mensajes ("la capacitación", "la visita").
 * - `guardarPadre()`: Promise de la entidad guardada (con `id` numérico); aquí van los asistentes.
 * - `eliminaciones`: uuids de imágenes guardadas marcadas para eliminar;
 *   `onEliminacionEstado(uuid, estado, payload)`: 'deleting' | 'done' | 'error' (payload = mensaje en español).
 * - `pendientes`: [{ uuid, file }] en orden; `onImagenEstado(uuid, estado, payload)` por cada imagen:
 *   'uploading' | 'done' (payload = item guardado del backend) | 'error' (payload = mensaje en español).
 * Devuelve { guardado, imagenes: { subidas, fallidas }, eliminaciones: { eliminadas: [uuid], fallidas: [{ uuid, error }] } }.
 * Un fallo de imagen (borrar o subir) NO lanza: el padre ya está guardado y se reintenta al volver a guardar / por imagen.
 * Lanza DomainError solo si falla el padre (no se tocó ninguna imagen).
 */
export const guardarConHijos = async ({
  entidad,
  tipo,
  guardarPadre,
  media,
  pendientes = [],
  eliminaciones = [],
  creado,
  onImagenEstado,
  onEliminacionEstado,
}) => {
  const guardado = await guardarPadre();
  if (!guardado.id) throw new DomainError(`El servidor no devolvió el identificador de ${entidad}`);

  // Secuencial: una por una, sin detenerse ante un fallo (las fallidas siguen marcadas y se reintentan en el siguiente guardado).
  const borradas = await ejecutarEliminaciones({ uuids: eliminaciones, media, onEstado: onEliminacionEstado });

  const imagenes = { subidas: [], fallidas: [] };
  if (pendientes.length) {
    // Secuencial y con espera entre subidas dentro de media.uploadEach (workaround de la colisión por time()).
    const { subidas, fallidas } = await media.uploadEach({
      tipo,
      idPadre: guardado.id,
      items: pendientes,
      creado,
      onItem: (item, estado, payload) => {
        if (!onImagenEstado) return;
        onImagenEstado(item.uuid, estado, estado === 'error' ? mensajeErrorSubida(payload) : payload);
      },
    });
    imagenes.subidas = subidas;
    imagenes.fallidas = fallidas.map(f => ({ uuid: f.uuid, error: mensajeErrorSubida(f.error) }));
  }
  return { guardado, imagenes, eliminaciones: borradas };
};

/**
 * Reintenta UNA imagen contra un padre ya guardado (no vuelve a guardar el formulario).
 * Mismo contrato de `onImagenEstado`; devuelve el item guardado o null si falló.
 */
export const reintentarImagen = async ({ tipo, idPadre, item, media, creado, onImagenEstado }) => {
  const { subidas } = await media.uploadEach({
    tipo,
    idPadre,
    items: [item],
    creado,
    onItem: (it, estado, payload) => {
      if (onImagenEstado) onImagenEstado(it.uuid, estado, estado === 'error' ? mensajeErrorSubida(payload) : payload);
    },
  });
  return subidas.length ? subidas[0].media || {} : null;
};

export default guardarConHijos;
